import { getPermissions, hasPermission } from "../../utilis/permissionStorage";
import { useEffect, useState } from "react";
import * as FaIcons from "react-icons/fa";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import Swal from "sweetalert2";

// Skeleton Loader
const SkeletonCard = () => (
  <div className="card border mb-3">
    <div className="card-header">
      <div className="d-flex align-items-center justify-content-between">
        <div>
          <h6 className="card-title mb-0 placeholder-glow">
            <span className="placeholder col-6"></span>
          </h6>
        </div>
      </div>
    </div>
    <div className="card-body">
      <div className="row row-cols-2 row-cols-md-4 row-cols-lg-7 g-3">
        {[1, 2, 3, 4, 5, 6, 7].map((i) => (
          <div className="col" key={i}>
            <div className="placeholder-glow">
              <span className="placeholder col-12"></span>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default function WeekendTab() {
  const [editingBranch, setEditingBranch] = useState(null);
  const [branches, setBranches] = useState([]);
  const [weekendConfigs, setWeekendConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSkeleton, setShowSkeleton] = useState(true);
  const [permissions, setPermissions] = useState({});

  const weekDays = [
    { value: 1, label: "Monday" },
    { value: 2, label: "Tuesday" },
    { value: 3, label: "Wednesday" },
    { value: 4, label: "Thursday" },
    { value: 5, label: "Friday" },
    { value: 6, label: "Saturday" },
    { value: 0, label: "Sunday" },
  ];

  // Fetch branches
  const fetchBranches = async () => {
    try {
      const response = await apiUrl.get("/getBranches");
      if (response?.status) {
        setBranches(response.data.data || []);
      }
    } catch (error) {
      console.error("Failed to load branches", error);
    }
  };

  // Fetch weekend configs
  const fetchWeekendConfigs = async () => {
    try {
      setShowSkeleton(true);
      const response = await apiUrl.get("/hr/policies/fetch-weekend-configs");
      if (response?.status) {
        setWeekendConfigs(response.data.data || []);
      }
    } catch (error) {
      console.error("Failed to load weekend configs", error);
    } finally {
      setShowSkeleton(false);
    }
  };

  useEffect(() => {
    fetchBranches();
    fetchWeekendConfigs();
  }, []);

  useEffect(() => {
    const perms = getPermissions();
    if (perms) {
      setPermissions(perms);
    }
  }, []);

  const mergedConfigs = branches.map((branch) => {
    const config = weekendConfigs.find(
      (c) => Number(c.branch_id) === Number(branch.id)
    );
    return {
      branch_id: Number(branch.id),
      branch_name: branch.agencyname || branch.name,
      weekend_days: config?.weekend_days || [],
      has_config: !!config,
    };
  });

  // Handle edit
  const handleEdit = (branchId) => {
    setEditingBranch(Number(branchId));
  };

  // Handle save
  const handleSave = async (branch_id) => {
    const config =
      weekendConfigs.find((c) => c.branch_id === branch_id) ||
      mergedConfigs.find((c) => c.branch_id === branch_id);

    if (!config || !config.weekend_days) return;

    setLoading(true);
    try {
      const response = await apiUrl.post("/hr/policies/save-weekend-config", {
        branch_id: branch_id,
        weekend_days: config.weekend_days,
      });

      if (response.data.status) {
        Swal.fire({
          title: "Saved!",
          text: "Weekend configuration updated",
          icon: "success",
          timer: 3000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
        setEditingBranch(null);
        fetchWeekendConfigs();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      Swal.fire(
        "Error",
        error.message || "Failed to save configuration",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setEditingBranch(null);
    fetchWeekendConfigs();
  };

  // Toggle weekend day
  const toggleWeekendDay = (branch_id, dayValue) => {
    setWeekendConfigs((prev) => {
      const existingConfigIndex = prev.findIndex(
        (c) => c.branch_id === branch_id
      );

      if (existingConfigIndex >= 0) {
        const updated = [...prev];
        const currentDays = updated[existingConfigIndex].weekend_days || [];
        const newDays = currentDays.includes(dayValue)
          ? currentDays.filter((d) => d !== dayValue)
          : [...currentDays, dayValue];
        updated[existingConfigIndex] = {
          ...updated[existingConfigIndex],
          weekend_days: newDays,
        };
        return updated;
      } else {
        return [
          ...prev,
          {
            branch_id: branch_id,
            weekend_days: [dayValue],
          },
        ];
      }
    });
  };

  // Get upcoming weekends
  const getUpcomingWeekends = (weekendDays) => {
    const today = new Date();
    const weekends = [];

    for (let i = 0; i < 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const dayValue = date.getDay();

      if (weekendDays.includes(dayValue)) {
        weekends.push(
          date.toLocaleDateString("en-US", {
            weekday: "short",
            month: "short",
            day: "numeric",
          })
        );
      }

      if (weekends.length >= 4) break;
    }
    return weekends;
  };

  // Get branch name
  const getBranchName = (branch_id) => {
    const branch = branches.find((b) => b.id === branch_id);
    return branch ? branch.agencyname || branch.name : `Branch ${branch_id}`;
  };

  return (
    <>
      {permissions["manage_policies"] === 1 && (
        <div className="container-fluid px-2 py-3">
          {/* Header */}
          <div className="card border mb-1">
            <div className="card-body d-flex justify-content-between align-items-center">
              <div>
                <h5 className="card-title d-flex align-items-center gap-2 mb-1">
                  <FaIcons.FaCalendarAlt className="fs-5" />
                  Weekend Configuration
                </h5>
                <p className="card-text small text-muted mb-0">
                  Configure which days are weekends for each branch
                </p>
              </div>
            </div>
          </div>

          {/* Weekend Settings List */}
          <div className="card border">
            <div className="card-header bg-light">
              <h6 className="mb-0">Branch Weekend Settings</h6>
            </div>
            <div className="card-body">
              {showSkeleton ? (
                <>
                  <SkeletonCard />
                  <SkeletonCard />
                  <SkeletonCard />
                </>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {mergedConfigs.map((config, index) => (
                    <div key={index} className="card border mb-2">
                      <div className="card-header">
                        <div className="d-flex align-items-center justify-content-between">
                          <div>
                            <h6 className="card-title mb-0">
                              {getBranchName(config.branch_id)}
                            </h6>
                            <div className="d-flex align-items-center gap-2 mt-1">
                              <span className="small text-primary">
                                {config.weekend_days?.length || 0} weekend day
                                {config.weekend_days?.length !== 1 ? "s" : ""}
                              </span>
                            </div>
                          </div>
                          <div className="d-flex gap-2">
                            {editingBranch === config.branch_id ? (
                              <>
                                <button
                                  className="btn btn-success btn-sm"
                                  onClick={() => handleSave(config.branch_id)}
                                  disabled={loading}
                                >
                                  {loading ? (
                                    <span className="spinner-border spinner-border-sm"></span>
                                  ) : (
                                    <>
                                      <FaIcons.FaSave className="me-1" /> Save
                                    </>
                                  )}
                                </button>
                                <button
                                  className="btn btn-outline-secondary btn-sm"
                                  onClick={handleCancel}
                                  disabled={loading}
                                >
                                  <FaIcons.FaTimes className="me-1" /> Cancel
                                </button>
                              </>
                            ) : (
                              <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => handleEdit(config.branch_id)}
                              >
                                <FaIcons.FaEdit className="me-1" /> Edit
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="card-body">
                        <div className="d-flex flex-column gap-3">
                          <div className="row row-cols-2 row-cols-md-4 row-cols-lg-7 g-3">
                            {weekDays.map((day) => (
                              <div key={day.value} className="col">
                                <div className="form-check d-flex align-items-center">
                                  <input
                                    style={{
                                      width: "17px",
                                      height: "17px",
                                    }}
                                    type="checkbox"
                                    className="form-check-input"
                                    id={`weekend-${config.branch_id}-${day.value}`}
                                    checked={
                                      config.weekend_days?.includes(
                                        day.value
                                      ) || false
                                    }
                                    onChange={() =>
                                      toggleWeekendDay(
                                        config.branch_id,
                                        day.value
                                      )
                                    }
                                    disabled={
                                      editingBranch !== config.branch_id
                                    }
                                  />
                                  <label
                                    className="form-check-label ms-2 large"
                                    htmlFor={`weekend-${config.branch_id}-${day.value}`}
                                  >
                                    {day.label.slice(0, 3)}
                                  </label>
                                </div>
                              </div>
                            ))}
                          </div>

                          {!editingBranch &&
                            config.weekend_days?.length > 0 && (
                              <div className="d-flex flex-column gap-3">
                                <div className="d-flex flex-wrap gap-2">
                                  {config.weekend_days.map((dayValue) => {
                                    const day = weekDays.find(
                                      (d) => d.value === dayValue
                                    );
                                    return (
                                      <span
                                        key={dayValue}
                                        className="badge bg-light border text-primary"
                                      >
                                        {day ? day.label : `Day ${dayValue}`}
                                      </span>
                                    );
                                  })}
                                </div>

                                <div className="p-3 bg-light border rounded">
                                  <div className="d-flex align-items-center gap-2 mb-2">
                                    <FaIcons.FaInfoCircle className="text-success" />
                                    <span className="small fw-medium">
                                      Upcoming Weekend Dates
                                    </span>
                                  </div>
                                  <div className="d-flex flex-wrap gap-2">
                                    {getUpcomingWeekends(
                                      config.weekend_days
                                    ).map((date, idx) => (
                                      <span
                                        key={idx}
                                        className="badge bg-success text-white"
                                      >
                                        {date}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
